import { inject, Injectable } from "@angular/core";
import { first, map } from "rxjs";
import { GraphService } from "@kernel/graphql/services/graph.service";
import { IPhotoTypeStats } from "../interfaces/photo-type-list.interface";
import { GraphqlMessageInterface } from "@app/kernel/graphql/interfaces/graphql-message.interface";
import { IPhotoType } from "../interfaces/photo-type.interface";
import { IPhotoTypeInput } from "../interfaces/photo-type-input.interface";
import { PHOTO_TYPE_LIST_QUERY } from "../queries/photo-type.query";
import { ListingGraphqlInput } from "@app/kernel/graphql/models";
import { PHOTO_TYPE_FORM_QUERY } from "../queries/photo-type-form.query";

/**
 * API service for interacting with photo type GraphQL operations.
 *
 * Provides methods for retrieving, creating, updating, enabling, disabling,
 * reordering, and deleting photo types.
 */
@Injectable()
export class PhotoTypeApiService {
  /** GraphQL service used for executing queries and mutations. */
  private readonly graphService = inject(GraphService);

  /**
   * Fetches a photo type by its ID.
   *
   * @param id - The ID of the photo type to retrieve.
   * @param dataShape - The shape of the GraphQL data to return. Defaults to the form query.
   * @returns An observable that emits the fetched `IPhotoType`.
   * @throws GraphQL error if the response contains errors.
   */
  getPhotoType(id: string, dataShape: string | string[] = PHOTO_TYPE_FORM_QUERY) {
    return this.graphService.constructQuery<{ getPhotoType: IPhotoType }>(dataShape, "getPhotoType", { id }).pipe(
      first(),
      map(data => {
        if (data.errors?.length) {
          throw data.errors[0];
        }
        return data.data.getPhotoType;
      })
    );
  }

  /**
   * Creates a photo type.
   *
   * @param input - The photo type input.
   * @param dataShape - The shape of the response data. Defaults to the form query.
   * @returns An observable emitting the created `IPhotoType`.
   * @throws GraphQL error if the response contains errors.
   */
  createPhotoType(input: IPhotoTypeInput, dataShape: string | string[] = PHOTO_TYPE_FORM_QUERY) {
    const shape = Array.isArray(dataShape) ? dataShape : [dataShape];
    return this.graphService
      .constructMutation<{
        createPhotoType: IPhotoType;
      }>("createPhotoType", { input: "PhotoTypeInput!" }, { input }, shape)
      .pipe(
        first(),
        map(data => {
          if (data.errors?.length) {
            throw data.errors[0];
          }
          return data.data?.createPhotoType;
        })
      );
  }

  /**
   * Updates a photo type.
   *
   * @param id - The ID of the photo type to update.
   * @param input - The photo type input.
   * @param dataShape - The shape of the response data. Defaults to the form query.
   * @returns An observable emitting the updated `IPhotoType`.
   * @throws GraphQL error if the response contains errors.
   */
  updatePhotoType(id: string, input: IPhotoTypeInput, dataShape: string | string[] = PHOTO_TYPE_FORM_QUERY) {
    const shape = Array.isArray(dataShape) ? dataShape : [dataShape];
    return this.graphService
      .constructMutation<{
        updatePhotoType: IPhotoType;
      }>("updatePhotoType", { id: "ID!", input: "PhotoTypeInput!" }, { id, input }, shape)
      .pipe(
        first(),
        map(data => {
          if (data.errors?.length) {
            throw data.errors[0];
          }
          return data.data?.updatePhotoType;
        })
      );
  }

  /**
   * Deletes a photo type by its ID.
   *
   * @param id - The ID of the photo type to delete.
   * @returns An observable emitting the GraphQL deletion response message.
   * @throws GraphQL error if the response contains errors.
   */
  deletePhotoType(id: string) {
    return this.graphService
      .constructMutation<{
        deletePhotoType: GraphqlMessageInterface;
      }>("deletePhotoType", { id: "ID!" }, { id }, ["text", "code"])
      .pipe(
        first(),
        map(data => {
          if (data.errors?.length) {
            throw data.errors[0];
          }
          return data.data?.deletePhotoType;
        })
      );
  }

  /**
   * Enables a photo type by its ID.
   *
   * @param id - The ID of the photo type to enable.
   * @param dataShape - The shape of the response data. Defaults to the form query.
   * @returns An observable emitting the updated `IPhotoType`.
   */
  enablePhotoType(id: string, dataShape: string | string[] = PHOTO_TYPE_FORM_QUERY) {
    const shape = Array.isArray(dataShape) ? dataShape : [dataShape];
    return this.graphService
      .constructMutation<{
        enablePhotoType: IPhotoType;
      }>("enablePhotoType", { id: "ID!" }, { id }, shape)
      .pipe(
        first(),
        map(data => {
          if (data.errors?.length) {
            throw data.errors[0];
          }
          return data.data?.enablePhotoType;
        })
      );
  }

  /**
   * Disables a photo type by its ID.
   *
   * @param id - The ID of the photo type to disable.
   * @param dataShape - The shape of the response data. Defaults to the form query.
   * @returns An observable emitting the updated `IPhotoType`.
   */
  disablePhotoType(id: string, dataShape: string | string[] = PHOTO_TYPE_FORM_QUERY) {
    const shape = Array.isArray(dataShape) ? dataShape : [dataShape];
    return this.graphService
      .constructMutation<{
        disablePhotoType: IPhotoType;
      }>("disablePhotoType", { id: "ID!" }, { id }, shape)
      .pipe(
        first(),
        map(data => {
          if (data.errors?.length) {
            throw data.errors[0];
          }
          return data.data?.disablePhotoType;
        })
      );
  }

  /**
   * Fetches photo types ordered by `order`.
   *
   * @param listingQuery - Optional listing query with filters/search.
   * @returns An observable emitting the array of `IPhotoType`.
   */
  listPhotoTypes(listingQuery?: ListingGraphqlInput) {
    return this.graphService.constructQuery<{ photoTypes: IPhotoType[] }>(PHOTO_TYPE_LIST_QUERY, "photoTypes", listingQuery || null).pipe(
      first(),
      map(data => {
        if (data.errors?.length) {
          throw data.errors[0];
        }
        return data.data.photoTypes;
      })
    );
  }

  /**
   * Reorders photo types.
   *
   * @param ids - The complete ordered list of photo type IDs.
   * @returns An observable emitting the GraphQL response.
   */
  reorderPhotoTypes(ids: string[]) {
    return this.graphService
      .constructMutation<{ reorderPhotoTypes: GraphqlMessageInterface }>("reorderPhotoTypes", { ids: "[ID!]!" }, { ids }, ["text", "code"])
      .pipe(
        first(),
        map(data => {
          if (data.errors?.length) {
            throw data.errors[0];
          }
          return data.data?.reorderPhotoTypes;
        })
      );
  }

  /**
   * Fetches photo type statistics.
   *
   * @returns An observable emitting the stats.
   */
  getPhotoTypeStats() {
    return this.graphService
      .constructQuery<{ getPhotoTypeStats: IPhotoTypeStats }>(
        `
          enabledCount
          requiredStepsCount
          totalCount
        `,
        "getPhotoTypeStats"
      )
      .pipe(
        first(),
        map(data => {
          if (data.errors?.length) {
            throw data.errors[0];
          }
          return data.data.getPhotoTypeStats;
        })
      );
  }
}
